import React, { useEffect, useRef } from 'react';
import { cn } from '@/lib';
import { ChatHeader } from './ChatHeader';
import { MessageList } from './MessageList';
import { ChatInput, ChatInputHandle } from './ChatInput';
import { ImagePreviewModal } from './ImagePreviewModal';
import { useAskStanley } from '../hooks/useAskStanley';
import { useChatDropdown, useChatScroll, useImagePreview } from '../hooks';

interface AskStanleyChatInterfaceProps {
    isOpen: boolean;
    onClose: () => void;
    className?: string;
}

export const AskStanleyChatInterface: React.FC<AskStanleyChatInterfaceProps> = ({
    isOpen,
    onClose,
    className = '',
}) => {
    const { chats, currentChatId, createNewChat, switchToChat } = useAskStanley();

    const currentChat = chats.find(chat => chat.id === currentChatId);
    const messages = currentChat?.messages || [];
    const messagesContainerRef = useChatScroll(messages);

    const { showChatDropdown, toggleChatDropdown, closeChatDropdown } = useChatDropdown();

    // Image preview functionality shared across all components
    const {
        isImagePreviewOpen,
        previewImageSrc,
        previewImageAlt,
        previewImageFilename,
        handlePreviewImage,
        handleCloseImagePreview,
    } = useImagePreview();

    const chatInputRef = useRef<ChatInputHandle>(null);

    // Focus input when chat opens
    useEffect(() => {
        if (isOpen) {
            chatInputRef.current?.focus();
        }
    }, [isOpen]);

    return (
        <div
            className={cn(
                'z-tooltip fixed inset-0 size-full transition-transform duration-300 ease-in-out',
                'lg:w-ask-stanley-lg lg:inset-auto lg:top-0 lg:right-0',
                '2xl:w-ask-stanley-2xl',
                isOpen ? 'translate-x-0' : 'translate-x-full',
                className
            )}
        >
            {/* Chat Panel */}
            <div className='bg-brand-dimmed border-brand-subtle flex h-full flex-col border shadow-xl max-lg:h-[calc(100%-var(--spacing-bottom-menu-bar))] lg:m-4 lg:ml-0 lg:h-[calc(100%-2rem)] lg:rounded-2xl'>
                <ChatHeader
                    currentChatTitle={currentChat?.title || ''}
                    onClose={onClose}
                    onCreateNewChat={createNewChat}
                    onChatSelect={switchToChat}
                    chats={chats}
                    showChatDropdown={showChatDropdown}
                    onToggleChatDropdown={toggleChatDropdown}
                    onCloseChatDropdown={closeChatDropdown}
                />

                <MessageList ref={messagesContainerRef} messages={messages} onPreviewImage={handlePreviewImage} />

                <ChatInput
                    ref={chatInputRef}
                    showSuggestions={messages.length === 0}
                    onPreviewImage={handlePreviewImage}
                />
            </div>

            {/* Image Preview Modal */}
            <ImagePreviewModal
                isOpen={isImagePreviewOpen}
                imageSrc={previewImageSrc}
                imageAlt={previewImageAlt}
                filename={previewImageFilename}
                onClose={handleCloseImagePreview}
            />
        </div>
    );
};
